import {
  readBootstrapPerformanceDiagnostics,
  writeBootstrapPerformanceDiagnostics,
} from '../domain/settings'

export type PerformanceDiagnosticKind =
  | 'activity'
  | 'long-task'
  | 'milestone'
  | 'module'
  | 'navigation'
  | 'paint'
  | 'resource'

export interface PerformanceDiagnosticEntry {
  id: number
  kind: PerformanceDiagnosticKind
  name: string
  startTime: number
  duration: number
  detail?: Record<string, string | number | boolean | null>
}

interface DiagnosticsBridge {
  enabled: boolean
  entries: PerformanceDiagnosticEntry[]
  nextId: number
  moduleLoaded: (name: string, startTime: number, endTime: number) => void
  record: (entry: Omit<PerformanceDiagnosticEntry, 'id'>) => void
}

declare global {
  var __MINIMANY_PERFORMANCE_DIAGNOSTICS__: DiagnosticsBridge | undefined
}

const MAX_ENTRIES = 800
const subscribers = new Set<() => void>()
let observers: PerformanceObserver[] = []
let removeActivityListeners: (() => void) | null = null
let version = 0

function notify(): void {
  version += 1
  for (const subscriber of subscribers) subscriber()
}

function append(entry: Omit<PerformanceDiagnosticEntry, 'id'>): void {
  const bridge = globalThis.__MINIMANY_PERFORMANCE_DIAGNOSTICS__
  if (!bridge?.enabled) return
  bridge.entries.push({ ...entry, id: bridge.nextId++ })
  if (bridge.entries.length > MAX_ENTRIES) {
    bridge.entries.splice(0, bridge.entries.length - MAX_ENTRIES)
  }
  notify()
}

function createBridge(existingEntries: PerformanceDiagnosticEntry[] = []): DiagnosticsBridge {
  const entries = existingEntries.slice(-MAX_ENTRIES)
  return {
    enabled: true,
    entries,
    nextId: (entries.at(-1)?.id ?? 0) + 1,
    moduleLoaded(name, startTime, endTime) {
      append({ kind: 'module', name, startTime, duration: Math.max(0, endTime - startTime) })
    },
    record: append,
  }
}

function safeResourceName(value: string): string {
  try {
    const url = new URL(value, location.href)
    return url.origin === location.origin ? url.pathname : `${url.origin}${url.pathname}`
  } catch {
    return value.split(/[?#]/, 1)[0] ?? 'resource'
  }
}

function observe(type: string, handle: (entry: PerformanceEntry) => void): void {
  if (typeof PerformanceObserver === 'undefined') return
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) handle(entry)
    })
    observer.observe({ type, buffered: true })
    observers.push(observer)
  } catch {
    // Unsupported entry types are expected across browsers.
  }
}

function describeTarget(target: EventTarget | null): Record<string, string | null> {
  if (!(target instanceof Element)) return { element: null, label: null }
  return {
    element: target.tagName.toLowerCase(),
    label: target.getAttribute('aria-label') || target.getAttribute('role'),
  }
}

function installActivityListeners(): () => void {
  const eventTypes = [
    'change',
    'click',
    'focusin',
    'input',
    'keydown',
    'pointerdown',
    'submit',
  ] as const
  const listener = (event: Event) => {
    const startedAt = performance.now()
    const detail = describeTarget(event.target)
    requestAnimationFrame(() => requestAnimationFrame(() => {
      append({
        kind: 'activity',
        name: event.type,
        startTime: startedAt,
        duration: performance.now() - startedAt,
        detail,
      })
    }))
  }
  for (const type of eventTypes) document.addEventListener(type, listener, { capture: true })
  return () => {
    for (const type of eventTypes) document.removeEventListener(type, listener, { capture: true })
  }
}

function installObservers(): void {
  observe('navigation', (entry) => append({
    kind: 'navigation',
    name: entry.name,
    startTime: entry.startTime,
    duration: entry.duration,
  }))
  observe('paint', (entry) => append({
    kind: 'paint',
    name: entry.name,
    startTime: entry.startTime,
    duration: entry.duration,
  }))
  observe('resource', (entry) => {
    const resource = entry as PerformanceResourceTiming
    append({
      kind: 'resource',
      name: safeResourceName(entry.name),
      startTime: entry.startTime,
      duration: entry.duration,
      detail: {
        initiator: resource.initiatorType || 'unknown',
        transferSize: resource.transferSize || 0,
      },
    })
  })
  observe('longtask', (entry) => append({
    kind: 'long-task',
    name: entry.name || 'main thread',
    startTime: entry.startTime,
    duration: entry.duration,
  }))
  removeActivityListeners = installActivityListeners()
}

function stopCollectors(): void {
  for (const observer of observers) observer.disconnect()
  observers = []
  removeActivityListeners?.()
  removeActivityListeners = null
}

export function initializePerformanceDiagnostics(): boolean {
  if (!readBootstrapPerformanceDiagnostics()) return false
  const existing = globalThis.__MINIMANY_PERFORMANCE_DIAGNOSTICS__
  globalThis.__MINIMANY_PERFORMANCE_DIAGNOSTICS__ = createBridge(existing?.entries)
  stopCollectors()
  installObservers()
  recordDiagnosticMilestone('diagnostics:initialized')
  return true
}

export function setPerformanceDiagnosticsEnabled(enabled: boolean): void {
  writeBootstrapPerformanceDiagnostics(enabled)
  if (!enabled) {
    stopCollectors()
    globalThis.__MINIMANY_PERFORMANCE_DIAGNOSTICS__ = undefined
    notify()
    return
  }
  globalThis.__MINIMANY_PERFORMANCE_DIAGNOSTICS__ = createBridge()
  installObservers()
  recordDiagnosticMilestone('diagnostics:enabled')
}

export function recordDiagnosticMilestone(
  name: string,
  detail?: PerformanceDiagnosticEntry['detail'],
): void {
  append({ kind: 'milestone', name, startTime: performance.now(), duration: 0, detail })
}

export async function measureDiagnosticActivity<T>(
  name: string,
  activity: () => Promise<T>,
): Promise<T> {
  const startedAt = performance.now()
  try {
    return await activity()
  } finally {
    append({
      kind: 'activity',
      name,
      startTime: startedAt,
      duration: performance.now() - startedAt,
    })
  }
}

export function getPerformanceDiagnostics(): readonly PerformanceDiagnosticEntry[] {
  return globalThis.__MINIMANY_PERFORMANCE_DIAGNOSTICS__?.entries ?? []
}

export function clearPerformanceDiagnostics(): void {
  const bridge = globalThis.__MINIMANY_PERFORMANCE_DIAGNOSTICS__
  if (!bridge) return
  bridge.entries.length = 0
  bridge.nextId = 1
  notify()
}

export function subscribeToPerformanceDiagnostics(subscriber: () => void): () => void {
  subscribers.add(subscriber)
  return () => subscribers.delete(subscriber)
}

export function getPerformanceDiagnosticsVersion(): number {
  return version
}
